import type { FormSegment } from '../../../../../../../../../modules/audiences/AudienceForm/types.ts';

export type SegmentState = {
  directSegments: FormSegment[];
  rtgSegments: FormSegment[];
  thirdPartySegments: FormSegment[];
  siteCategorySegments: FormSegment[];
  oxPurchaseSegments: FormSegment[];
};

export const ActionType = {
  AddSegment: 'add-segment',
  RemoveSegment: 'remove-segment',
  RemoveAll: 'remove-all',
} as const;

export type SegmentAction =
  | { type: typeof ActionType.AddSegment; bucket: keyof SegmentState; segment: FormSegment }
  | { type: typeof ActionType.RemoveSegment; bucket: keyof SegmentState; id: string }
  | { type: typeof ActionType.RemoveAll; bucket?: keyof SegmentState };

export const getSegmentBucket = (segment: FormSegment): keyof SegmentState => {
  if (segment.category === 'direct') {
    return 'directSegments';
  }

  if (segment.category === 'predefined') {
    if (segment.sub_category === 'partner_audiences') return 'rtgSegments';
    if (segment.sub_category === 'third_party_data') return 'thirdPartySegments';
    if (segment.sub_category === 'past_browsing_interests') return 'siteCategorySegments';
    if (segment.sub_category === 'ox_purchase_data') return 'oxPurchaseSegments';
  }

  // If we reach here, the segment doesn't match any known category/sub_category
  // This should not happen in normal operation, but we'll default to directSegments
  return 'directSegments';
};

export const segmentReducer = (state: SegmentState, action: SegmentAction): SegmentState => {
  switch (action.type) {
    case ActionType.AddSegment: {
      const bucket = state[action.bucket];
      const existingIndex = bucket.findIndex(s => s.id === action.segment.id);

      if (existingIndex >= 0) {
        const updatedBucket = [...bucket];
        updatedBucket[existingIndex] = action.segment;
        return { ...state, [action.bucket]: updatedBucket };
      }

      return { ...state, [action.bucket]: [...bucket, action.segment] };
    }

    case ActionType.RemoveSegment: {
      const bucket = state[action.bucket];

      return {
        ...state,
        [action.bucket]: bucket.filter(segment => segment.id !== action.id),
      };
    }

    case ActionType.RemoveAll: {
      if (action.bucket) {
        return { ...state, [action.bucket]: [] };
      }

      return {
        directSegments: [],
        rtgSegments: [],
        thirdPartySegments: [],
        siteCategorySegments: [],
        oxPurchaseSegments: [],
      };
    }

    default:
      return state;
  }
};
